"use client";

import { useMemo } from "react";

import { FadeInIntersection } from "@/common/components";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/common/components-2/accordion";
import { Button } from "@/common/components-2/button";
import { MigrationLink } from "@/common/components-2/migration-link";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { Section } from "@/common/components-2/section";
import { SectionHeading } from "@/common/components-2/section-heading";
import { useTranslations } from "@/services/i18n";

export function FaqSection() {
  const t = useTranslations("pages.consultations.faq");

  const items = useMemo(
    () => [
      { title: t("items.1.title"), description: t("items.1.description") },
      { title: t("items.2.title"), description: t.raw("items.2.description") as string },
      { title: t("items.3.title"), description: t.raw("items.3.description") as string },
      { title: t("items.4.title"), description: t("items.4.description") },
      { title: t("items.5.title"), description: t("items.5.description") },
      { title: t("items.6.title"), description: t("items.6.description") },
      { title: t("items.7.title"), description: t("items.7.description") },
      { title: t("items.8.title"), description: t("items.8.description") },
    ],
    [t],
  );

  return (
    <FadeInIntersection rootMargin="0px 0px -300px 0px" delay={100} asChild animation="fadeUp">
      <Section classes={{ section: "py-10 md:py-20 lg:py-24" }}>
        <SectionHeading
          heading={t.rich("heading")}
          title={{ text: t("title") }}
          classes={{ container: "lg:mx-0", heading: "lg:text-left", title: "max-lg:justify-center" }}
        />
        <div className="mx-auto max-w-[640px] text-center">
          <Accordion type="single" collapsible className="text-left">
            {items.map(({ title, description }, index) => (
              <AccordionItem
                key={title}
                value={title}
                className="animate-fill-forwards opacity-0 group-[.animate-intersected-in]/intersected:animate-fade-right"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <AccordionTrigger className="py-5 font-sans text-2xl lg:text-2xl">{title}</AccordionTrigger>
                <AccordionContent>
                  <SanitizedContent as="div" content={description} className="[&_ul]:list-inside [&_ul]:list-disc" />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-8 text-center text-lg lg:mt-16">{t("info")}</p>
          <Button className="mt-6 w-full lg:mt-8 lg:w-auto" variant="secondary" asChild size="lg">
            <MigrationLink href="https://pomoc.upacjenta.pl/support/home">{t("button")}</MigrationLink>
          </Button>
        </div>
      </Section>
    </FadeInIntersection>
  );
}
